import { useEffect, useRef, useState } from 'react'
import styles from './CountdownTimer.module.css'

export interface CountdownTimerProps {
  targetDate?: Date
  timeRemainingMs?: number
  onComplete?: () => void
}

export default function CountdownTimer({
  targetDate,
  timeRemainingMs,
  onComplete,
}: CountdownTimerProps) {
  const [remaining, setRemaining] = useState(() => {
    if (targetDate) return Math.max(0, targetDate.getTime() - Date.now())
    return timeRemainingMs ?? 0
  })

  const startTimeRef = useRef(Date.now())
  const initialMsRef = useRef(timeRemainingMs ?? 0)

  useEffect(() => {
    if (timeRemainingMs !== undefined && !targetDate) {
      initialMsRef.current = timeRemainingMs
      startTimeRef.current = Date.now()
      setRemaining(timeRemainingMs)
    }
  }, [timeRemainingMs, targetDate])

  useEffect(() => {
    const interval = setInterval(() => {
      const newRemaining = targetDate
        ? Math.max(0, targetDate.getTime() - Date.now())
        : Math.max(0, initialMsRef.current - (Date.now() - startTimeRef.current))

      setRemaining(newRemaining)
      if (newRemaining === 0) {
        clearInterval(interval)
        onComplete?.()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [targetDate, onComplete])

  const totalSeconds = Math.floor(remaining / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  function Unit({ value, unit }: { value: number; unit: string }) {
    return (
      <div className={styles.unit}>
        <span className={styles.value}>{value}</span>
        <span className={styles.label}>{unit}</span>
      </div>
    )
  }

  return (
    <div className={styles.timer}>
      <Unit value={days} unit="DAYS" />
      <span className={styles.sep}>:</span>
      <Unit value={hours} unit="HOURS" />
      <span className={styles.sep}>:</span>
      <Unit value={minutes} unit="MINUTES" />
      <span className={styles.sep}>:</span>
      <Unit value={seconds} unit="SECONDS" />
    </div>
  )
}
